import { faWifiFair, faWifiStrong, faWifiWeak } from '@fortawesome/pro-solid-svg-icons';

import { useTranslate } from '@growtherapy/i18n';
import { Body, IconBadge, IconBadgeUse, cn } from '@growtherapy/sprout-ui';

import './signal-animation.css';

import { RemoteParticipantReconnectingOverlayViewModel } from './types';

function AnimatedSignalIconBadge({ variation }: Pick<RemoteParticipantReconnectingOverlayViewModel, 'variation'>) {
  const { className, ...signalIconBadgeProps } = {
    'aria-hidden': true,
    use: IconBadgeUse.Warning,
    className: cn('col-start-1 row-start-1', {
      'text-xl p-6': variation !== 'mini',
    }),
  };

  return (
    <div
      className={cn('grid place-items-center', {
        'pb-4': variation !== 'mini',
      })}
    >
      <IconBadge {...signalIconBadgeProps} icon={faWifiWeak} className={cn(className, 'signal-weak')} />
      <IconBadge
        {...signalIconBadgeProps}
        icon={faWifiFair}
        // This icon is slighly off center
        iconClassName='ml-[1px]'
        className={cn(className, 'signal-fair')}
      />
      <IconBadge {...signalIconBadgeProps} icon={faWifiStrong} className={cn(className, 'signal-strong')} />
    </div>
  );
}

function Messaging({
  variation,
  participantDisplayName,
}: Pick<RemoteParticipantReconnectingOverlayViewModel, 'variation' | 'participantDisplayName'>) {
  const t = useTranslate();
  const reconnectText = (
    <Body.Default
      data-testid='reconnecting-user-text'
      className='text-neutral-100 text-center rebrand:text-text-inverse'
    >
      {participantDisplayName
        ? t('telehealth-core:video.reconnectingName', {
            name: participantDisplayName,
          })
        : t('telehealth-core:video.reconnectingUser')}
    </Body.Default>
  );

  switch (variation) {
    case 'multi-participant':
      return reconnectText;
    case 'single-participant':
      return (
        <>
          {reconnectText}
          <Body.Xsmall
            data-testid='session-ending-warning-text'
            className='text-neutral-100 text-xs text-center rebrand:text-text-inverse'
          >
            {t('telehealth-core:video.sessionMayEndIfUnableToReconnect')}
          </Body.Xsmall>
        </>
      );
    case 'mini':
    default:
      return null;
  }
}

export function RemoteParticipantReconnectingOverlay({
  participantDisplayName,
  variation,
}: RemoteParticipantReconnectingOverlayViewModel) {
  return (
    <div className='absolute isolate w-full h-full flex flex-col items-center justify-center transition-all bg-neutral-900 bg-clip-padding backdrop-filter backdrop-blur-3xl bg-opacity-80 rebrand:bg-surface-inverse'>
      <AnimatedSignalIconBadge variation={variation} />
      <Messaging variation={variation} participantDisplayName={participantDisplayName} />
    </div>
  );
}
